import * as React from 'react';
import Link from 'next/link';

import { isCrossHostHref } from '@/lib/app-surface-href';

export type SurfaceLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  /** Relative (same host) or absolute (cross-host) destination. */
  readonly href: string;
  /** Same-host only; forwarded to `next/link`. */
  readonly prefetch?: boolean | null;
  /** Same-host only; forwarded to `next/link`. */
  readonly replace?: boolean;
  /** Same-host only; forwarded to `next/link`. */
  readonly scroll?: boolean;
};

/**
 * `next/link` for a same-host href, a native `<a>` for a cross-host one
 * (WEB-1231). There is no client route to push across origins, and a `<Link>`
 * to a path the proxy 307s cross-host gets RSC-prefetched and blocked by
 * `connect-src` (WEB-834). Making the `<a>` structural keeps "no `?_rsc=`
 * request" from depending on a `prefetch={false}` prop a refactor could drop.
 */
export const SurfaceLink = React.forwardRef<HTMLAnchorElement, SurfaceLinkProps>(
  function SurfaceLink({ href, prefetch, replace, scroll, ...rest }, ref) {
    if (isCrossHostHref(href)) {
      return <a ref={ref} href={href} {...rest} />;
    }
    return (
      <Link ref={ref} href={href} prefetch={prefetch} replace={replace} scroll={scroll} {...rest} />
    );
  },
);
